import { type SocketMachineContext } from '../../../network-state-machine-context';
import {
  type ReconciledEvent,
  type SyncEvent,
  type SyncFetchingEvent,
} from '../../../network-state-machine-events';

export async function syncingFetchSnapshotAsync<
  T extends Pick<SocketMachineContext, 'relayDetails' | 'socketFetchSnapshot'>
>(ctx: T, signal: AbortSignal): Promise<SyncFetchingEvent> {
  if (!ctx.relayDetails) {
    throw new Error('Missing relay details in context');
  }
  const remoteDocument = await ctx.socketFetchSnapshot(signal);
  return {
    type: 'synced',
    remoteDocument,
    relayDetails: ctx.relayDetails,
  };
}

export async function syncingFetchStepsAsync<
  T extends Pick<
    SocketMachineContext,
    'relayDetails' | 'remoteDocument' | 'socketFetchSteps' | 'debounce'
  >
>(ctx: T, signal: AbortSignal): Promise<SyncFetchingEvent> {
  if (!ctx.relayDetails) {
    throw new Error('Missing relay details in context');
  }
  if (!ctx.remoteDocument) {
    throw new Error('Missing remote document in context');
  }

  const remoteDocument = await ctx.socketFetchSteps(
    ctx.remoteDocument,
    ctx.debounce.onlineAttempts,
    signal
  );

  return {
    type: 'synced',
    remoteDocument,
    relayDetails: ctx.relayDetails,
  };
}

export async function syncingApplyChangesAsync<
  T extends Pick<
    SocketMachineContext,
    'relayDetails' | 'remoteDocument' | 'socketApplyChanges'
  >
>(ctx: T): Promise<SyncFetchingEvent> {
  if (!ctx.relayDetails) {
    throw new Error('Missing relay details in context');
  }
  if (!ctx.remoteDocument) {
    throw new Error('Missing remote document in context');
  }

  const remoteDocument = ctx.socketApplyChanges(ctx.relayDetails.url, ctx.remoteDocument);

  return {
    type: 'synced',
    remoteDocument,
    relayDetails: ctx.relayDetails,
  };
}

export async function reconcileOfflineChangesAsync<
  T extends Pick<SocketMachineContext, 'reconcileOfflineChanges' | 'remoteDocument'>
>(ctx: T): Promise<ReconciledEvent> {
  if (ctx.remoteDocument) {
    await ctx.reconcileOfflineChanges?.(ctx.remoteDocument);
  }

  return {
    type: 'reconciled',
  };
}

export async function syncingUpdateChangesAsync<
  T extends Pick<
    SocketMachineContext,
    'remoteDocument' | 'socketUpdateChanges' | 'debounce' | 'pendingMessages'
  >
>(ctx: T, signal: AbortSignal): Promise<SyncEvent> {
  // We don't want any pending messages at this point, as any pending updates
  // should be possible to derive from this step and the syncing step
  // otherwise we just end up with duplicate messages
  ctx.pendingMessages.dequeueAll();

  if (!ctx.remoteDocument) {
    throw new Error('Missing remote document in context');
  }

  ctx.socketUpdateChanges(ctx.remoteDocument, ctx.debounce.onlineAttempts, signal);

  return {
    type: 'synced',
  };
}
